import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { MethodSource } from '@/components/admin/protocoles/reading/use-method';
import type { ReadingLine } from '@/lib/analyst-reading';
import {
  ANALYST_PASS_MESSAGE_LINE,
  ANALYST_PASS_METHOD_TOGGLE,
  ANALYST_PASS_PART_LABEL,
  METHOD_FAILED_BODY,
  METHOD_MISSING_BODY,
  METHOD_READING_BODY,
  methodBadge,
  readingLines,
} from '@/lib/analyst-reading';
import { cn } from '@/lib/utils';
import type {
  AnalystPass,
  AnalystPassPart,
  AnalystPassPartKind,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  METHOD_SEED,
  functionsSystemParts,
  hasAnalystReading,
  styleSystemParts,
} from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import {
  BookOpenText,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Lock,
  Settings,
} from 'lucide-react';
import { useId, useState } from 'react';

/**
 * « Ce que la passe 1 recevra » et « Ce que la passe 2 recevra » - le système de
 * chaque passe de l'analyste, en lecture seule, à la frappe (analyste.md §3.4, §5.1,
 * §7.2, N41 ; planches `AnalysteA` et `AnalysteAEtats`).
 *
 * ## Rendered by the SERVER's functions, one pass at a time
 *
 * The four parts come from `functionsSystemParts` and `styleSystemParts` of
 * `shared`, the functions the server assembles the two systems it serves with
 * (N41): the panel and « l'entrée servie » cannot part company, and no sentence of
 * a framing, of a method or of a contract is written in this package. The reading
 * is handed in TRIMMED, as the save stores it and as the server therefore serves
 * it, and RESOLVED by the same call - every `{{objectif:id}}` becomes the name the
 * draft gives that objective, in plain text in the violet part, « (objectif
 * retiré) » for one the draft no longer carries (#158, N50).
 *
 * ONLY the panel of the active tab is drawn (§3.4, « ne rends que l'aperçu de la
 * passe active »): the two systems are assembled on every keystroke otherwise, and
 * the tabs say which of the two readings the operator is typing. What is kept
 * across a trip between the tabs is each pass's own folds.
 *
 * It shows the system and nothing else: no fingerprint - a hash of a draft nobody
 * has served names nothing - and no matter, which depends on a session. The line
 * under the panel says what the MESSAGE carries, since no panel can show it.
 *
 * ## Four parts under INTERFACE headings, three of them not the protocol's
 *
 * « Le cadre » and « Ce que tu rends » are written in CODE, greyed, folded, badged
 * « en code »; « Les trois fonctions » / « Les six styles » are written in Réglages
 * > Méthode, greyed and badged with the version the panel read - the « latest dev »,
 * the one an operator tunes -, folded on a preview under a fade with a button that
 * unfolds it, since it is the part long enough that folding it away would hide that
 * there is a method at all. The protocol's own part is the one that never folds,
 * framed in violet: it is what this section exists to set. Empty, its frame turns
 * to a dashed line and says so.
 *
 * ## Méthode may be missing, and nothing breaks
 *
 * Not read yet, without a version on this side (a vitrine before its first
 * « Passer en production », §4.5), or whose read FAILED: the part says which in
 * place of its body - a failed read is never left on « Lecture de Méthode… » for
 * ever, and never told as a missing version -, and the other three are drawn all
 * the same, assembled on the seed of the method - only the body of that part comes
 * from Méthode, and it is the one the note stands for.
 *
 * ## The protocol's part exactly as served, the others set in type
 *
 * The parts written in code and by Méthode go through `readingLines` - a bullet's
 * hanging indent, one pause between paragraphs - because they are fixed texts read
 * as a method. The protocol's own part does NOT (N41, review of !474): it is shown
 * character for character as the analyst receives it, blank lines and all, in
 * `whitespace-pre-wrap` - a panel that tidied what somebody typed would show a
 * reading the analyst never gets.
 *
 * ## A folded preview says nothing to a screen reader
 *
 * The preview of Méthode's part is a VISUAL cue - its first lines under a fade, the
 * rest of the text clipped but present. Read aloud it would be the whole of the
 * method behind a « réduit » button, so while folded it is `aria-hidden`: the two
 * buttons that unfold it are what a screen reader hears.
 *
 * ## A region that scrolls, and that a keyboard can reach
 *
 * The panel is `sticky` beside the long left column and its parts scroll inside it
 * (the maquette's 760 px, or what the window leaves under the bar and the head); a
 * zone a pointer can scroll and a keyboard cannot is a contract one reads the first
 * five lines of - hence the tab stop on the region, the arbitration
 * `criterion-card-popover.tsx` already states.
 */

export interface AnalystPassPanelProps {
  /** The pass whose system is drawn: the one of the active tab. */
  readonly pass: AnalystPass;
  /** The reading of that pass as the draft holds it - the panel trims it as the save would. */
  readonly reading: string;
  /** The steps its citations resolve on - the draft's, or the version « Voir » shows (#158). */
  readonly steps: readonly ProtocolStepDraft[];
  /** What the panel read of Méthode, the « latest dev »: its part, and the version it names. */
  readonly method: MethodSource;
}

/** The parts that fold: the two written in code, and the one Méthode wrote. */
type FoldingKind = Exclude<AnalystPassPartKind, 'reading'>;
type Folds = Readonly<Record<FoldingKind, boolean>>;

/** What the part of Méthode says in place of its body, by what the read of Méthode came to. */
const METHOD_PLACEHOLDER: Readonly<Record<Exclude<MethodSource['state'], 'read'>, string>> = {
  reading: METHOD_READING_BODY,
  missing: METHOD_MISSING_BODY,
  failed: METHOD_FAILED_BODY,
};

/** How the maquette opens them: all three folded - the method on a preview, the two others on their heading. */
const OPENING: Folds = { framing: false, method: false, contract: false };

/** The system of one pass as parts, assembled on the seed of the method when Méthode has not been read. */
function partsOf(
  pass: AnalystPass,
  reading: string,
  steps: readonly ProtocolStepDraft[],
  method: MethodSource,
): readonly AnalystPassPart[] {
  const content = method.state === 'read' ? method.content : METHOD_SEED;
  return pass === 1
    ? functionsSystemParts({ reading, steps, functions: content.functions })
    : styleSystemParts({ reading, steps, styles: content.styles });
}

export function AnalystPassPanel({ pass, reading, steps, method }: AnalystPassPanelProps) {
  const titleId = useId();
  // Each pass keeps its own folds: toggling « Les trois fonctions » says nothing of the styles.
  const [folds, setFolds] = useState<Readonly<Record<AnalystPass, Folds>>>({
    1: OPENING,
    2: OPENING,
  });
  const parts = partsOf(pass, reading.trim(), steps, method);

  return (
    <div className="flex min-w-0 flex-col gap-2 lg:sticky lg:top-[calc(var(--spacing-admin-bar)+var(--editor-head-height,0px)+1rem)]">
      <section
        aria-labelledby={titleId}
        data-testid={PROTOCOL_TEST_IDS.analystPreview}
        data-pass={pass}
        className="flex min-h-0 min-w-0 flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 p-4"
      >
        <div className="flex shrink-0 flex-col gap-1">
          <h3 id={titleId} className="font-semibold text-base text-bleu-nuit">
            Ce que la passe {pass} recevra
          </h3>
          <p className="text-gray-600 text-xs leading-4">
            Le prompt de la passe {pass}, assemblé à la frappe. Rien ne se modifie ici.
          </p>
        </div>
        <section
          aria-label={`Prompt assemblé de la passe ${pass}`}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
          tabIndex={0}
          className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:max-h-[min(760px,calc(100dvh-var(--spacing-admin-bar)-var(--editor-head-height,0px)-9rem))]"
        >
          {parts.map((part) => {
            if (part.kind === 'reading') {
              return <ProtocolPart key={part.kind} pass={pass} body={part.body} />;
            }
            // Held in a constant: the narrowing of `part.kind` does not survive
            // into the closure below, and the record is keyed on the three.
            const kind = part.kind;
            const toggle = () =>
              setFolds((was) => ({
                ...was,
                [pass]: { ...was[pass], [kind]: !was[pass][kind] },
              }));
            return kind === 'method' ? (
              <MethodPart
                key={kind}
                pass={pass}
                part={part}
                method={method}
                open={folds[pass].method}
                onToggle={toggle}
              />
            ) : (
              <CodePart
                key={kind}
                pass={pass}
                part={part}
                kind={kind}
                open={folds[pass][kind]}
                onToggle={toggle}
              />
            );
          })}
        </section>
      </section>
      <p data-testid={PROTOCOL_TEST_IDS.analystMessage} className="text-gray-600 text-xs leading-4">
        {ANALYST_PASS_MESSAGE_LINE[pass]}
      </p>
    </div>
  );
}

/** A part's lines: bullets with their hanging indent, pauses between paragraphs. */
export function PartLines({ lines }: { lines: readonly ReadingLine[] }) {
  return (
    <>
      {lines.map((line) => {
        if (line.kind === 'gap') return <span key={line.at} aria-hidden className="block h-2" />;
        if (line.kind === 'bullet') {
          return (
            <p key={line.at} className="flex gap-1.5">
              <span aria-hidden className="shrink-0">
                -
              </span>
              <span className="min-w-0 break-words">{line.text}</span>
            </p>
          );
        }
        return (
          <p key={line.at} className="whitespace-pre-wrap break-words">
            {line.text}
          </p>
        );
      })}
    </>
  );
}

/** The grey pill of a part the protocol did not write: « en code », or the version of Méthode. */
const GREY_PILL =
  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-pill bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 text-micro';

/** The heading line of a folding part: its chevron button on the left, what wrote it on the right. */
const HEAD_BUTTON =
  'inline-flex min-h-7 cursor-pointer items-center gap-1.5 font-semibold text-gray-600 text-xs uppercase tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface CodePartProps {
  readonly pass: AnalystPass;
  readonly part: AnalystPassPart;
  /** The part's kind, narrowed - `part.kind` cannot index the folds without it. */
  readonly kind: 'framing' | 'contract';
  readonly open: boolean;
  readonly onToggle: () => void;
}

/** One part written in code: greyed, folded to its heading by its chevron, badged « en code ». */
function CodePart({ pass, part, kind, open, onToggle }: CodePartProps) {
  const bodyId = useId();
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.readingPart}
      data-kind={kind}
      data-open={open}
      className="flex shrink-0 flex-col gap-1.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
          className={HEAD_BUTTON}
        >
          <Chevron aria-hidden className="size-3.5" />
          <span>{ANALYST_PASS_PART_LABEL[pass][kind]}</span>
        </button>
        <span className={GREY_PILL}>
          <Lock aria-hidden className="size-3" />
          en code
        </span>
      </div>
      <div id={bodyId} hidden={!open} className="text-caption text-gray-600">
        <PartLines lines={readingLines(part.body)} />
      </div>
    </div>
  );
}

interface MethodPartProps {
  readonly pass: AnalystPass;
  readonly part: AnalystPassPart;
  readonly method: MethodSource;
  readonly open: boolean;
  readonly onToggle: () => void;
}

/**
 * The part Réglages > Méthode wrote - « Les trois fonctions » or « Les six styles »:
 * greyed, badged with the version read, folded on a PREVIEW (its first lines under a
 * fade, and a button that unfolds it) because it is the one part long enough that
 * folding it away entirely would hide that there is a method at all.
 *
 * The version is the pointer the section reads, « latest dev »: the one an operator
 * is tuning, and not necessarily the one a session will freeze (§3.4, §4.5).
 *
 * Beside the badge, « Ouvrir » is a typed link to the tab where Méthode is written
 * (§3.4, planches `AnalysteA` and `AnalysteAEtats`). It opens a NEW tab: the editor
 * of a protocol that does not exist yet keeps its draft in its page alone (§7.1), and
 * a navigation drops the autosave still owed, so leaving would lose the words being
 * typed - the reason `ProtocolReadingLink` has for its `newTab`, said the same way:
 * to the eye by the icon, to a screen reader by the name. It is offered whatever the
 * read of Méthode came to - the tab is where a missing or an unreadable version is
 * told.
 */
function MethodPart({ pass, part, method, open, onToggle }: MethodPartProps) {
  const bodyId = useId();
  const Chevron = open ? ChevronDown : ChevronRight;
  const read = method.state === 'read';

  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.readingPart}
      data-kind="method"
      data-open={open}
      className="flex shrink-0 flex-col gap-1.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          disabled={!read}
          onClick={onToggle}
          className={cn(HEAD_BUTTON, !read && 'cursor-default')}
        >
          <Chevron aria-hidden className="size-3.5" />
          <span>{ANALYST_PASS_PART_LABEL[pass].method}</span>
        </button>
        <span className="inline-flex shrink-0 items-center gap-2">
          <span
            data-testid={PROTOCOL_TEST_IDS.analystMethodBadge}
            data-state={method.state}
            className={GREY_PILL}
          >
            <Settings aria-hidden className="size-3" />
            {methodBadge(read ? method.number : null)}
          </span>
          <Link
            to="/admin/reglages/methode"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-sm font-semibold text-bleu-nuit text-xs leading-4 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Ouvrir
            <ExternalLink aria-hidden className="size-3 shrink-0" />
            <span className="sr-only"> Réglages &gt; Méthode (nouvel onglet)</span>
          </Link>
        </span>
      </div>
      {read ? (
        <>
          <div
            id={bodyId}
            // The folded preview is a visual cue: its clipped text stays unread.
            aria-hidden={open ? undefined : true}
            className={cn(
              'text-caption text-gray-600',
              !open && 'relative max-h-20 overflow-hidden',
            )}
          >
            <PartLines lines={readingLines(part.body)} />
            {open ? null : (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-9 bg-linear-to-b from-transparent to-gray-50"
              />
            )}
          </div>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={onToggle}
            className="-ml-2 inline-flex h-7 cursor-pointer items-center gap-1.5 self-start rounded-md px-2 font-semibold text-bleu-nuit text-caption hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {open ? (
              <ChevronUp aria-hidden className="size-3.5" />
            ) : (
              <ChevronDown aria-hidden className="size-3.5" />
            )}
            {open ? ANALYST_PASS_METHOD_TOGGLE[pass].close : ANALYST_PASS_METHOD_TOGGLE[pass].open}
          </button>
        </>
      ) : (
        <p id={bodyId} className="text-caption text-gray-600 italic">
          {METHOD_PLACEHOLDER[method.state]}
        </p>
      )}
    </div>
  );
}

/**
 * The protocol's part - the one that is THIS section's to set: its heading in
 * violet, never folded, the reading framed in violet and drawn AS SERVED (see
 * the head of this file); dashed, and saying there is none, when the pass has no
 * reading (spaces alone included, N35).
 */
function ProtocolPart({ pass, body }: { pass: AnalystPass; body: string }) {
  const empty = !hasAnalystReading(body);
  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.readingPart}
      data-kind="reading"
      data-open="true"
      className="flex shrink-0 flex-col gap-1.5"
    >
      <div className="flex min-h-7 flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <h4 className="font-semibold text-violet-deep text-xs uppercase tracking-wide">
          {ANALYST_PASS_PART_LABEL[pass].reading}
        </h4>
        <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-pill bg-violet-tint px-2 py-0.5 font-semibold text-micro text-violet-deep">
          <BookOpenText aria-hidden className="size-3" />
          ce protocole
        </span>
      </div>
      <div
        data-testid={PROTOCOL_TEST_IDS.readingServed}
        data-empty={empty}
        className={cn(
          'rounded-md border-2 border-violet-deep bg-white px-3 py-2.5 text-caption',
          empty ? 'border-dashed text-violet-deep italic' : 'text-gray-800',
        )}
      >
        {empty ? (
          <p>Pas de lecture pour la passe {pass}.</p>
        ) : (
          <p className="whitespace-pre-wrap break-words">{body}</p>
        )}
      </div>
    </div>
  );
}
